import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { all_routes } from "../../router/all_routes";
import { Link, useNavigate } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { Checkbox, DatePicker, Input, Select } from "antd";
import CommonSelect from "../../../core/common/addEmployeecommonselect";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import useDepartment from "../../../core/api/useDepartment";
import useDesignations from "../../../core/api/useDesignations";
import { useRoles, getRolesList } from "../../../core/api/useRoles";
import useCreateEmployee from "../../../core/api/useCreateEmployee";
import { jobTypeOptions } from "../../../core/hardcoded/jobTypes";
import dayjs from "dayjs";
import useDeleteEmployee from "../../../core/api/useDeleteEmployee";
import { API_BASE_PATH } from "../../../environment";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import EmployeeExportImport from "./employeeExport";
import { useAuthToken } from "../../../core/data/redux/useAuthToken";

type Employee = {
  key: string;
  EmpId: string;
  Name: string;
  Email: string;
  Phone: string;
  Designation: string;
  JoiningDate: string;
  Status: string;
  Image: string;
  CurrentRole: string;
  employee_status?: string; // ✅ for stats
  join_date?: string; // ✅ for stats
  password: string;
};

type Department = {
  department_name: string;
  employee_count?: number;
  status?: string;
};

type Designation = {
  designation: string;
  department: string;
  totalEmployees?: number;
  status?: string;
  createdAt?: string;
};

type Role = {
  role_name: string;
  status?: string;
};

type EmployeeFormType = {
  firstname: string;
  lastName: string;
  employeeId: string;
  joiningDate: string;
  username: string;
  email: string;
  password: string;
  phone: string;
  company: string;
  role: string;
  department: string;
  designation: string;
  jobType: string;
  image: File | null;
  imagePreview: string;
};

type SelectOption = {
  label: string;
  value: string;
};

type SelectProps = {
  className?: string;
  options: SelectOption[];
  value: SelectOption; // ✅ This is what's missing
  onChange: (selected: SelectOption) => void;
};

type ModalFilters = {
  empId: string[];
  empName: string[];
  role: string[];
  designation: string[];
};

const EmployeeList = () => {
  const BASE_PATH = API_BASE_PATH;
  const { createEmployee, isLoading: isCreating } = useCreateEmployee();
  const { deleteEmployee } = useDeleteEmployee();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const authRole = useSelector((state: any) => state.auth.role);
  const navigate = useNavigate();
  const empToken = useAuthToken();
  // Add selected employees state for export functionality
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);

  const [visiblePasswords, setVisiblePasswords] = useState<
    Record<string, boolean>
  >({});

  // Filter Modal State
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedEmpIds, setSelectedEmpIds] = useState<string[]>([]);
  const [selectedEmpNames, setSelectedEmpNames] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedDesignations, setSelectedDesignations] = useState<string[]>(
    []
  );

  const [modalFilters, setModalFilters] = useState<ModalFilters>({
    empId: [],
    empName: [],
    role: [],
    designation: [],
  });

  const modalRef = useRef<HTMLDivElement>(null);

  // Initial form state
  const initialFormState = {
    firstname: "",
    lastName: "",
    employeeId: "",
    joiningDate: "",
    username: "",
    email: "",
    password: "",
    phone: "",
    company: "",
    role: "",
    department: "",
    designation: "",
    jobType: "",
    image: null,
    imagePreview: "",
  };

  const [employeeForm, setEmployeeForm] =
    useState<EmployeeFormType>(initialFormState);
  const [imageError, setImageError] = useState<string>("");

  const togglePasswordVisibility = (empId: string) => {
    setVisiblePasswords((prev) => ({
      ...prev,
      [empId]: !prev[empId],
    }));
  };

  // Add refresh handler for import functionality
  const handleRefreshData = () => {
    fetchEmployees();
    setSelectedEmployeeIds([]); // Clear selections after refresh
  };

  // Reset form function
  const resetForm = () => {
    setEmployeeForm(initialFormState);
    setImageError("");
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      const maxSize = 4 * 1024 * 1024; // 4MB
      if (file.size > maxSize) {
        setImageError("Image size should be less than 4MB.");
        // Reset the input value so the same file can trigger onChange again
        e.target.value = "";
        return;
      }
      setImageError(""); // Clear previous error
      setEmployeeForm((prev) => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file),
      }));
    }
    // Optional: reset input for other edge cases
    e.target.value = "";
  };

  let handleSubmit = async () => {
    if (
      !employeeForm.firstname ||
      !employeeForm.email ||
      !employeeForm.username ||
      !employeeForm.employeeId ||
      !employeeForm.joiningDate ||
      !employeeForm.role ||
      !employeeForm.department ||
      !employeeForm.designation ||
      !employeeForm.jobType
    ) {
      toast.warning("Please fill all required fields.");
      return;
    }
    let formData = new FormData();
    formData.append("firstname", employeeForm.firstname);
    formData.append("lastname", employeeForm.lastName);
    formData.append("employee_id", employeeForm.employeeId);
    formData.append("join_date", employeeForm.joiningDate); // ✅ FIXED DATE
    formData.append("username", employeeForm.username);
    formData.append("email", employeeForm.email);
    formData.append("password", employeeForm.password);
    formData.append("phone", employeeForm.phone);
    formData.append("company", employeeForm.company);
    formData.append("role_name", employeeForm.role);
    formData.append("department_name", employeeForm.department);
    formData.append("designation_name", employeeForm.designation);
    formData.append("job_type_name", employeeForm.jobType);

    if (employeeForm.image) {
      formData.append("photo", employeeForm.image);
    }
    try {
      await createEmployee(formData, fetchEmployees, empToken); // ✅ refresh table
      resetForm(); // Reset form after successful submission
      const addModal = document.getElementById("add_employee");
      if (addModal) {
        const bsAddModal = (window as any).bootstrap.Modal.getInstance(
          addModal
        );
        bsAddModal?.hide();
      }
    } catch (error) {
      console.error(error); // ✅ keep console for debugging
    }
  };

  {
    /*  Employe Create Part   */
  }
  const { getNormalizedDepartments } = useDepartment(empToken);
  const { fetchDesignationsAllInfo } = useDesignations(empToken);
  const { roles, fetchRoles } = useRoles(); // ✅ Hook usage

  const [department, setDepartment] = useState<
    { value: string; label: string }[]
  >([{ value: "Select", label: "Select" }]);
  const [designation, setDesignation] = useState<
    { value: string; label: string }[]
  >([{ value: "Select", label: "Select" }]);
  const [roleName, setRoleName] = useState<{ value: string; label: string }[]>([
    { value: "Select", label: "Select" },
  ]);

  const [jobType, setJobType] = useState(jobTypeOptions);

  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [searchText, setSearchText] = useState("");
  const [selectedDesignationFilter, setSelectedDesignationFilter] = useState<
    string | null
  >(null);

  const [tableKey, setTableKey] = useState<number>(0);

  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null
  );

  useEffect(() => {
    fetchEmployees();
  }, []);

  const BASE_URL = API_BASE_PATH;
  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/register/admin/all`, {
        headers: {
          accept: "application/json",
          authorization: `Bearer ${empToken}`,
        },
      });

      const employeesWithImages = await Promise.all(
        res.data.map(async (emp: any) => {
          let imageUrl = "/assets/img/defaultavatar.png";
          if (emp.photo) {
            try {
              const imgRes = await axios.get(
                `${BASE_URL}/register/employee/${
                  emp.employee_id
                }/image?t=${Date.now()}`,
                {
                  headers: { Authorization: `Bearer ${empToken}` },
                  responseType: "blob", // so we can convert to object URL
                }
              );
              const reader = new FileReader();
              return new Promise<string>((resolve) => {
                reader.onloadend = () => {
                  resolve(reader.result as string);
                };
                reader.readAsDataURL(imgRes.data);
              }).then((dataUrl) => {
                return {
                  ...emp,
                  imageUrl: dataUrl,
                };
              });
            } catch {
              return {
                ...emp,
                imageUrl: "/assets/img/defaultavatar.png",
              };
            }
          } else {
            return {
              ...emp,
              imageUrl,
            };
          }
        })
      );

      setAllEmployees(
        employeesWithImages.map(
          (emp: any): Employee => ({
            key: emp.employee_id,
            EmpId: emp.employee_id,
            Name: emp.firstname,
            Email: emp.email,
            Phone: emp.phone,
            Designation: emp.designation_name || "-",
            JoiningDate: emp.join_date,
            Status: emp.employee_status || "Active",
            Image: emp.imageUrl, // ✅ now has usable data URL
            CurrentRole: emp.role_name || "-",
            password: emp.temp_password,
          })
        )
      );
    } catch (err) {
      console.error("Failed to fetch employees:", err);
    }
  };

  // Handle individual checkbox selection
  const handleEmployeeSelect = (employeeId: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedEmployeeIds((prev) => [...prev, employeeId]);
    } else {
      setSelectedEmployeeIds((prev) => prev.filter((id) => id !== employeeId));
    }
  };

  // Handle select all checkbox
  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedEmployeeIds(allEmployees.map((emp) => emp.EmpId));
    } else {
      setSelectedEmployeeIds([]);
    }
  };

  // Filter Modal Functions
  const showModal = () => {
    setModalFilters({
      empId: selectedEmpIds,
      empName: selectedEmpNames,
      role: selectedRoles,
      designation: selectedDesignations,
    });
    setIsModalVisible(true);
  };

  const handleModalOk = () => {
    setSelectedEmpIds(modalFilters.empId);
    setSelectedEmpNames(modalFilters.empName);
    setSelectedRoles(modalFilters.role);
    setSelectedDesignations(modalFilters.designation);
    setIsModalVisible(false);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const clearFilters = () => {
    setSelectedEmpIds([]);
    setSelectedEmpNames([]);
    setSelectedRoles([]);
    setSelectedDesignations([]);
    setModalFilters({
      empId: [],
      empName: [],
      role: [],
      designation: [],
    });
    setSearchText("");
  };

  // Generate filter options from employees, sorted
  const empIdOptions = [
    { value: "", label: "All" },
    ...Array.from(new Set(allEmployees.map((e) => e.EmpId)))
      .sort()
      .map((id) => ({
        value: id,
        label: id,
      })),
  ];

  const empNameOptions = [
    { value: "", label: "All" },
    ...Array.from(new Set(allEmployees.map((e) => e.Name)))
      .sort()
      .map((name) => ({
        value: name,
        label: name,
      })),
  ];

  const roleOptions = [
    { value: "", label: "All" },
    ...Array.from(new Set(allEmployees.map((e) => e.CurrentRole)))
      .filter((role) => role !== "-")
      .sort()
      .map((role) => ({
        value: role,
        label: role,
      })),
  ];

  const designationOptions = [
    { value: "", label: "All" },
    ...Array.from(new Set(allEmployees.map((e) => e.Designation)))
      .filter((des) => des !== "-")
      .sort()
      .map((des) => ({
        value: des,
        label: des,
      })),
  ];

  // Filtered options without 'All'
  const filteredEmpIdOptions = empIdOptions.filter((opt) => opt.value !== "");
  const filteredEmpNameOptions = empNameOptions.filter(
    (opt) => opt.value !== ""
  );
  const filteredRoleOptions = roleOptions.filter((opt) => opt.value !== "");
  const filteredDesignationOptions = designationOptions.filter(
    (opt) => opt.value !== ""
  );

  // Select All handlers
  const handleSelectAllEmpId = (checked: boolean) => {
    if (checked) {
      setModalFilters((prev) => ({
        ...prev,
        empId: filteredEmpIdOptions.map((opt) => opt.value),
      }));
    } else {
      setModalFilters((prev) => ({ ...prev, empId: [] }));
    }
  };

  const handleSelectAllEmpName = (checked: boolean) => {
    if (checked) {
      setModalFilters((prev) => ({
        ...prev,
        empName: filteredEmpNameOptions.map((opt) => opt.value),
      }));
    } else {
      setModalFilters((prev) => ({ ...prev, empName: [] }));
    }
  };

  const handleSelectAllRole = (checked: boolean) => {
    if (checked) {
      setModalFilters((prev) => ({
        ...prev,
        role: filteredRoleOptions.map((opt) => opt.value),
      }));
    } else {
      setModalFilters((prev) => ({ ...prev, role: [] }));
    }
  };

  const handleSelectAllDesignation = (checked: boolean) => {
    if (checked) {
      setModalFilters((prev) => ({
        ...prev,
        designation: filteredDesignationOptions.map((opt) => opt.value),
      }));
    } else {
      setModalFilters((prev) => ({ ...prev, designation: [] }));
    }
  };

  // Indeterminate states
  const empIdIndeterminate =
    modalFilters.empId.length > 0 &&
    modalFilters.empId.length < filteredEmpIdOptions.length;
  const empNameIndeterminate =
    modalFilters.empName.length > 0 &&
    modalFilters.empName.length < filteredEmpNameOptions.length;
  const roleIndeterminate =
    modalFilters.role.length > 0 &&
    modalFilters.role.length < filteredRoleOptions.length;
  const designationIndeterminate =
    modalFilters.designation.length > 0 &&
    modalFilters.designation.length < filteredDesignationOptions.length;

  const empIdChecked =
    modalFilters.empId.length === filteredEmpIdOptions.length;
  const empNameChecked =
    modalFilters.empName.length === filteredEmpNameOptions.length;
  const roleChecked = modalFilters.role.length === filteredRoleOptions.length;
  const designationChecked =
    modalFilters.designation.length === filteredDesignationOptions.length;

  // Dropdown render for select all
  const dropdownRender = (
    menus: React.ReactNode,
    selectAllHandler: (checked: boolean) => void,
    checked: boolean,
    indeterminate: boolean,
    label: string
  ) => (
    <div style={{ maxHeight: 300, overflow: "auto" }}>
      <div style={{ padding: "8px", borderBottom: "1px solid #f0f0f0" }}>
        <Checkbox
          indeterminate={indeterminate}
          checked={checked}
          onChange={(e) => selectAllHandler(e.target.checked)}
        >
          Select All {label}
        </Checkbox>
      </div>
      <div style={{ maxHeight: 250, overflow: "auto" }}>{menus}</div>
    </div>
  );

  const filteredEmployees = allEmployees.filter((emp) => {
    const matchesSearch =
      emp.EmpId.toLowerCase().includes(searchText.toLowerCase()) ||
      emp.Name.toLowerCase().includes(searchText.toLowerCase()) ||
      emp.Email.toLowerCase().includes(searchText.toLowerCase()) ||
      emp.Phone.toLowerCase().includes(searchText.toLowerCase()) ||
      emp.Designation.toLowerCase().includes(searchText.toLowerCase()) ||
      emp.CurrentRole.toLowerCase().includes(searchText.toLowerCase());

    const matchesEmpId =
      selectedEmpIds.length === 0 || selectedEmpIds.includes(emp.EmpId);
    const matchesEmpName =
      selectedEmpNames.length === 0 || selectedEmpNames.includes(emp.Name);
    const matchesRole =
      selectedRoles.length === 0 || selectedRoles.includes(emp.CurrentRole);
    const matchesDesignation =
      selectedDesignations.length === 0 ||
      selectedDesignations.includes(emp.Designation);

    return (
      matchesSearch &&
      matchesEmpId &&
      matchesEmpName &&
      matchesRole &&
      matchesDesignation
    );
  });

  const activeFiltersCount = useMemo(() => {
    return (
      (selectedEmpIds.length > 0 ? 1 : 0) +
      (selectedEmpNames.length > 0 ? 1 : 0) +
      (selectedRoles.length > 0 ? 1 : 0) +
      (selectedDesignations.length > 0 ? 1 : 0)
    );
  }, [
    selectedEmpIds.length,
    selectedEmpNames.length,
    selectedRoles.length,
    selectedDesignations.length,
  ]);

  useEffect(() => {
    if (activeFiltersCount === 0) {
      setTableKey((prev) => prev + 1);
    }
  }, [activeFiltersCount]);

  const columns = [
    {
      title: "Emp ID",
      dataIndex: "EmpId",
      fixed: "left",
      width: 150,
      render: (text: string, record: Employee) => (
        <Link to={all_routes.employeedetails} state={{ employee: record }}>
          {text}
        </Link>
      ),
      sorter: (a: Employee, b: Employee) => a.EmpId.localeCompare(b.EmpId),
    },
    {
      title: "Name",
      dataIndex: "Name",
      fixed: "left",
      width: 250,
      render: (_: string, record: Employee) => (
        <div className="d-flex align-items-center">
          <Link
            to={all_routes.employeedetails}
            state={{ employee: record }}
            className="avatar avatar-md"
          >
            <img
              src={record.Image}
              className="img-fluid rounded-circle"
              alt="/assets/img/defaultavatar.png"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = "/assets/img/defaultavatar.png";
              }}
            />
          </Link>
          <div className="ms-2">
            <p className="text-dark mb-0">
              <Link
                to={all_routes.employeedetails}
                state={{ employee: record }}
              >
                {record.Name}
              </Link>
            </p>
            <span className="fs-12">{record.CurrentRole}</span>
          </div>
        </div>
      ),
      sorter: (a: Employee, b: Employee) => a.Name.localeCompare(b.Name),
    },
    {
      title: "Email",
      dataIndex: "Email",
      width: 350,
      sorter: (a: Employee, b: Employee) => a.Email.localeCompare(b.Email),
    },
    {
      title: "Phone",
      dataIndex: "Phone",
      width: 120,
      sorter: (a: Employee, b: Employee) => a.Phone.localeCompare(b.Phone),
    },
    {
      title: "Designation",
      dataIndex: "Designation",
      width: 200,
      render: (text: string) => (
        <span className="badge bg-secondary text-capitalize">{text}</span>
      ),
      sorter: (a: Employee, b: Employee) =>
        a.Designation.localeCompare(b.Designation),
    },
    {
      title: "Joining Date",
      dataIndex: "JoiningDate",
      width: 140,
      sorter: (a: Employee, b: Employee) =>
        new Date(a.JoiningDate).getTime() - new Date(b.JoiningDate).getTime(),
    },
    {
      title: "Status",
      dataIndex: "Status",
      width: 100,
      render: (text: string) => (
        <span
          className={`badge ${
            text === "Active" ? "badge-success" : "badge-danger"
          } d-inline-flex align-items-center badge-xs`}
        >
          <i className="ti ti-point-filled me-1" />
          {text}
        </span>
      ),
      sorter: (a: Employee, b: Employee) => a.Status.localeCompare(b.Status),
    },
    // ✅ Password Column – Only for Admin
    ...(authRole === "Admin"
      ? [
          {
            title: "Password",
            dataIndex: "Password",
            width: 140,
            render: (_: string, record: Employee) => {
              const isVisible = visiblePasswords[record.EmpId];
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontFamily: "monospace" }}>
                    {isVisible ? record.password : "••••••••"}
                  </span>
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => togglePasswordVisibility(record.EmpId)}
                  >
                    <i
                      className={`ti ${isVisible ? "ti-lock-open" : "ti-lock"}`}
                    />
                  </button>
                </div>
              );
            },
          },
        ]
      : []),
    {
      title: "Actions",
      fixed: "right",
      width: 100,
      render: (_: any, record: Employee) => (
        <div className="action-icon d-inline-flex">
          <button
            className="btn btn-outline-light p-1 me-2"
            onClick={() =>
              navigate(all_routes.employeedetails, {
                state: { employee: record },
              })
            }
          >
            <i className="ti ti-edit" />
          </button>

          <button
            className="btn btn-outline-light p-1"
            onClick={() => {
              setSelectedEmployee(record);
              setShowDeleteModal(true);
            }}
          >
            <i className="ti ti-trash" />
          </button>
        </div>
      ),
    },
  ];

  const getModalContainer = () => {
    const modalElement = document.getElementById("modal-datepicker");
    return modalElement || document.body;
  };

  // Level-3 scoped cache
  let departmentCache: Department[] | null = null;
  let roleCache: Role[] | null = null;
  let designationCache: Designation[] | null = null;
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        if (!departmentCache) {
          const fetchedDepartments = await getNormalizedDepartments();
          departmentCache = fetchedDepartments;
        }

        if (departmentCache) {
          const departmentOptions = departmentCache.map((item) => ({
            value: item.department_name,
            label: item.department_name,
          }));
          setDepartment([
            { value: "Select", label: "Select" },
            ...departmentOptions,
          ]);
        }

        // ✅ Roles using getRolesList
        if (!roleCache) {
          const fetchedRoles = await getRolesList(empToken);
          roleCache = fetchedRoles;
        }

        if (roleCache) {
          const roleOptions = roleCache.map((item: Role) => ({
            value: item.role_name,
            label: item.role_name,
          }));
          setRoleName([{ value: "Select", label: "Select" }, ...roleOptions]);
        }

        // ✅ Designations
        if (!designationCache) {
          const fetchedDesignations = await fetchDesignationsAllInfo();
          designationCache = fetchedDesignations;
        }

        if (designationCache) {
          const designationOptions = designationCache.map((item) => ({
            value: item.designation,
            label: item.designation,
          }));
          setDesignation([
            { value: "Select", label: "Select" },
            ...designationOptions,
          ]);
        }
      } catch (error) {
        console.error("Dropdown fetch error:", error);
      }
    };

    loadDropdowns();
  }, []);

  const totalEmployees = allEmployees.length;
  const activeEmployees = allEmployees.filter(
    (e) => e.Status === "Active"
  ).length;
  const inactiveEmployees = allEmployees.filter(
    (e) => e.Status !== "Active"
  ).length;

  // Optional: New Joiners in last 30 days
  const newJoiners = allEmployees.filter((e) => {
    if (!e.JoiningDate) return false;
    const joinDate = new Date(e.JoiningDate);
    const now = new Date();
    const daysAgo30 = new Date(now.setDate(now.getDate() - 30));
    return joinDate >= daysAgo30;
  }).length;

  const handleDelete = async () => {
    if (!selectedEmployee?.EmpId) {
      return;
    }

    try {
      await deleteEmployee(selectedEmployee.EmpId, empToken); // ✅ Pass correct ID
      await fetchEmployees(); // ✅ Refresh table
      setSelectedEmployee(null); // Clear selection
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };
  const hasNumbers = (str: string) => /\d/.test(str);

  const [nameErrors, setNameErrors] = useState<Record<string, string>>({});

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Employee</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Employee List
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="me-2 mb-2">
                <div className="d-flex align-items-center border bg-white rounded p-1 me-2 icon-list">
                  <Link
                    to={all_routes.employeeList}
                    className="btn btn-icon btn-sm active bg-primary text-white me-1"
                  >
                    <i className="ti ti-list-tree" />
                  </Link>
                  <Link
                    to={all_routes.employeeGrid}
                    className="btn btn-icon btn-sm"
                  >
                    <i className="ti ti-layout-grid" />
                  </Link>
                </div>
              </div>
              {/* Replace the old export dropdown with the new EmployeeExportImport component */}
              <EmployeeExportImport
                selectedEmployeeIds={selectedEmployeeIds}
                onRefreshData={handleRefreshData}
              />
              <div className="mb-2">
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center"
                  onClick={() => setShowAddModal(true)}
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add Employee
                </button>
              </div>
              <div className="ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          <div className="row">
            {/* Total Plans */}
            <div className="col-lg-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center overflow-hidden">
                    <div>
                      <span className="avatar avatar-lg bg-dark rounded-circle">
                        <i className="ti ti-users" />
                      </span>
                    </div>
                    <div className="ms-2 overflow-hidden">
                      <p className="fs-12 fw-medium mb-1 text-truncate">
                        Total Employee
                      </p>
                      <h4>{totalEmployees}</h4>
                    </div>
                  </div>
                  <div>
                    {/* <span className="badge badge-soft-purple badge-sm fw-normal">
                      <i className="ti ti-arrow-wave-right-down" />
                      +19.01%
                    </span> */}
                  </div>
                </div>
              </div>
            </div>
            {/* /Total Plans */}
            {/* Total Plans */}
            <div className="col-lg-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center overflow-hidden">
                    <div>
                      <span className="avatar avatar-lg bg-success rounded-circle">
                        <i className="ti ti-user-share" />
                      </span>
                    </div>
                    <div className="ms-2 overflow-hidden">
                      <p className="fs-12 fw-medium mb-1 text-truncate">
                        Active
                      </p>
                      <h4>{activeEmployees}</h4>
                    </div>
                  </div>
                  <div>
                    {/* <span className="badge badge-soft-primary badge-sm fw-normal">
                      <i className="ti ti-arrow-wave-right-down" />
                      +19.01%
                    </span> */}
                  </div>
                </div>
              </div>
            </div>
            {/* /Total Plans */}
            {/* Inactive Plans */}
            <div className="col-lg-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center overflow-hidden">
                    <div>
                      <span className="avatar avatar-lg bg-danger rounded-circle">
                        <i className="ti ti-user-pause" />
                      </span>
                    </div>
                    <div className="ms-2 overflow-hidden">
                      <p className="fs-12 fw-medium mb-1 text-truncate">
                        InActive
                      </p>
                      <h4>{inactiveEmployees}</h4>
                    </div>
                  </div>
                  <div>
                    {/* <span className="badge badge-soft-dark badge-sm fw-normal">
                      <i className="ti ti-arrow-wave-right-down" />
                      +19.01%
                    </span> */}
                  </div>
                </div>
              </div>
            </div>
            {/* /Inactive Companies */}
            {/* No of Plans  */}
            <div className="col-lg-3 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center overflow-hidden">
                    <div>
                      <span className="avatar avatar-lg bg-info rounded-circle">
                        <i className="ti ti-user-plus" />
                      </span>
                    </div>
                    <div className="ms-2 overflow-hidden">
                      <p className="fs-12 fw-medium mb-1 text-truncate">
                        New Joiners
                      </p>
                      <h4>{newJoiners}</h4>
                    </div>
                  </div>
                  <div>
                    {/* <span className="badge badge-soft-secondary badge-sm fw-normal">
                      <i className="ti ti-arrow-wave-right-down" />
                      +19.01%
                    </span> */}
                  </div>
                </div>
              </div>
            </div>
            {/* /No of Plans */}
          </div>
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Employee List</h5>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <button
                  className="btn btn-outline-primary me-3 d-flex align-items-center position-relative "
                  onClick={showModal}
                  aria-label={`Open filters${
                    activeFiltersCount > 0
                      ? ` (${activeFiltersCount} active)`
                      : ""
                  }`}
                  type="button"
                >
                  <i className="ti ti-filter me-1" aria-hidden="false"></i>
                  Filters
                  {activeFiltersCount > 0 && (
                    <span
                      className="badge bg-primary text-white position-absolute top-0 start-100 translate-middle ms-1 rounded-circle"
                      style={{ fontSize: "0.75em" }}
                    >
                      {activeFiltersCount}
                    </span>
                  )}
                </button>

                <Input.Search
                  type="text"
                  placeholder="Search name..."
                  allowClear
                  style={{ width: 300 }}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                {/* <div className="dropdown">
                  <button
                    className="btn btn-white dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    {selectedDesignationFilter || "Designation"}
                  </button>
                  <ul className="dropdown-menu">
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() => setSelectedDesignationFilter(null)}
                      >
                        All
                      </button>
                    </li>
                    {designation
                      .filter((item) => item.value !== "Select")
                      .map((item) => (
                        <li key={item.value}>
                          <button
                            className="dropdown-item"
                            onClick={() =>
                              setSelectedDesignationFilter(item.value)
                            }
                          >
                            {item.label}
                          </button>
                        </li>
                      ))}
                  </ul>
                </div> */}
              </div>
            </div>
            <div className="card">
              <div className="card-body p-0">
                <Table
                  key={tableKey}
                  dataSource={filteredEmployees}
                  columns={columns}
                  Selection={true}
                  scroll={{ x: 1500 }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025 © IATT.</p>
          <p>
            Designed &amp; Developed By{" "}
            <Link to="#" className="text-primary">
              IATT
            </Link>
          </p>
        </div>
        {/* Filter Modal */}
        <div
          className={`modal fade ${isModalVisible ? "show d-block" : ""}`}
          tabIndex={-1}
          role="dialog"
          aria-labelledby="filterModalLabel"
          aria-hidden={!isModalVisible}
          key={isModalVisible ? "filter-open" : "filter-closed"}
          style={{
            display: isModalVisible ? "block" : "none",
            backgroundColor: isModalVisible ? "rgba(0,0,0,0.5)" : "transparent",
          }}
          onClick={handleModalCancel}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered"
            role="document"
            style={{ maxWidth: "60vw", width: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content" ref={modalRef}>
              <div className="modal-header">
                <h5 className="modal-title" id="filterModalLabel">
                  Apply Filters
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleModalCancel}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body p-0">
                <div className="row g-3 px-3 py-3">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Employee ID</label>
                    <Select
                      mode="multiple"
                      style={{ width: "100%", color: "#000" }}
                      placeholder="Select Employee ID"
                      value={modalFilters.empId}
                      onChange={(value) =>
                        setModalFilters((prev) => ({ ...prev, empId: value }))
                      }
                      options={filteredEmpIdOptions || []}
                      allowClear
                      showSearch
                      maxTagCount={2}
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      getPopupContainer={() => modalRef.current!}
                      dropdownRender={(menus) =>
                        dropdownRender(
                          menus,
                          handleSelectAllEmpId,
                          empIdChecked,
                          empIdIndeterminate,
                          "Employee IDs"
                        )
                      }
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Employee Name</label>
                    <Select
                      mode="multiple"
                      style={{ width: "100%", color: "#000" }}
                      placeholder="Select Employee Name"
                      value={modalFilters.empName}
                      onChange={(value) =>
                        setModalFilters((prev) => ({ ...prev, empName: value }))
                      }
                      options={filteredEmpNameOptions || []}
                      allowClear
                      showSearch
                      maxTagCount={2}
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      getPopupContainer={() => modalRef.current!}
                      dropdownRender={(menus) =>
                        dropdownRender(
                          menus,
                          handleSelectAllEmpName,
                          empNameChecked,
                          empNameIndeterminate,
                          "Employee Names"
                        )
                      }
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Role</label>
                    <Select
                      mode="multiple"
                      style={{ width: "100%", color: "#000" }}
                      placeholder="Select Role"
                      value={modalFilters.role}
                      onChange={(value) =>
                        setModalFilters((prev) => ({ ...prev, role: value }))
                      }
                      options={filteredRoleOptions || []}
                      allowClear
                      showSearch
                      maxTagCount={2}
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      getPopupContainer={() => modalRef.current!}
                      dropdownRender={(menus) =>
                        dropdownRender(
                          menus,
                          handleSelectAllRole,
                          roleChecked,
                          roleIndeterminate,
                          "Roles"
                        )
                      }
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Designation</label>
                    <Select
                      mode="multiple"
                      style={{ width: "100%", color: "#000" }}
                      placeholder="Select Designation"
                      value={modalFilters.designation}
                      onChange={(value) =>
                        setModalFilters((prev) => ({
                          ...prev,
                          designation: value,
                        }))
                      }
                      options={filteredDesignationOptions || []}
                      allowClear
                      showSearch
                      maxTagCount={2}
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      getPopupContainer={() => modalRef.current!}
                      dropdownRender={(menus) =>
                        dropdownRender(
                          menus,
                          handleSelectAllDesignation,
                          designationChecked,
                          designationIndeterminate,
                          "Designations"
                        )
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-white me-2"
                  onClick={clearFilters}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleModalOk}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
        {isModalVisible && (
          <div
            className="modal-backdrop fade show"
            onClick={handleModalCancel}
          ></div>
        )}
      </div>

      {/* /Page Wrapper */}

      {showAddModal && (
        <>
          <div className="modal fade show d-block" role="dialog" tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <div className="d-flex align-items-center">
                    <h4 className="modal-title me-2">Add New Employee</h4>
                  </div>
                  <button
                    type="button"
                    className="btn-close custom-btn-close"
                    onClick={() => setShowAddModal(false)}
                    aria-label="Close"
                  >
                    <i className="ti ti-x" />
                  </button>
                </div>

                <form onSubmit={(e) => e.preventDefault()}>
                  <div className="contact-grids-tab">
                    <ul className="nav nav-underline" id="myTab" role="tablist">
                      <li className="nav-item" role="presentation">
                        <button
                          className="nav-link active"
                          id="info-tab"
                          type="button"
                          role="tab"
                        >
                          Basic Information
                        </button>
                      </li>
                    </ul>
                  </div>

                  <div className="tab-content" id="myTabContent">
                    <div
                      className="tab-pane fade show active"
                      id="basic-info"
                      role="tabpanel"
                      aria-labelledby="info-tab"
                      tabIndex={0}
                    >
                      <div className="modal-body pb-0">
                        <div className="row">
                          {/* 🔽 Image Upload with Preview */}
                          <div className="col-md-12">
                            <div className="d-flex align-items-center flex-wrap row-gap-3 bg-light w-100 rounded p-3 mb-4">
                              <div className="d-flex align-items-center justify-content-center avatar avatar-xxl rounded-circle border border-dashed me-2 flex-shrink-0 text-dark frames">
                                {employeeForm.imagePreview ? (
                                  <img
                                    src={employeeForm.imagePreview}
                                    alt="Preview"
                                    className="img-fluid rounded-circle"
                                    style={{
                                      width: "80px",
                                      height: "80px",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <i className="ti ti-photo text-gray-2 fs-16" />
                                )}
                              </div>
                              <div className="profile-upload">
                                <div className="mb-2">
                                  <h6 className="mb-1">Upload Profile Image</h6>
                                  <p className="fs-12">
                                    Image should be below 4 mb
                                  </p>
                                </div>
                                <div className="profile-uploader d-flex align-items-center">
                                  <div className="drag-upload-btn btn btn-sm btn-primary me-2">
                                    Upload
                                    <input
                                      type="file"
                                      className="form-control image-sign"
                                      accept="image/*"
                                      onChange={handleImageChange}
                                    />
                                  </div>
                                  <Link to="#" className="btn btn-light btn-sm">
                                    Cancel
                                  </Link>
                                </div>
                                {imageError && (
                                  <p className="text-danger mt-2 mb-0 fs-12">
                                    {imageError}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* 🔽 Controlled Input Fields */}
                          {[
                            {
                              label: "First Name",
                              required: true,
                              name: "firstname",
                            },
                            {
                              label: "Last Name",
                              required: false,
                              name: "lastName",
                            },
                            {
                              label: "Employee ID",
                              required: true,
                              name: "employeeId",
                            },
                            {
                              label: "Username",
                              required: true,
                              name: "username",
                            },
                            {
                              label: "Email",
                              required: true,
                              name: "email",
                              type: "email",
                            },
                            {
                              label: "Phone Number",
                              required: true,
                              name: "phone",
                            },
                            {
                              label: "Company",
                              required: true,
                              name: "company",
                            },
                          ].map((field, index) => (
                            <div className="col-md-6" key={index}>
                              <div className="mb-3">
                                <label className="form-label">
                                  {field.label}
                                  {field.required && (
                                    <span className="text-danger"> *</span>
                                  )}
                                </label>
                                <input
                                  type={field.type || "text"}
                                  className="form-control"
                                  value={(employeeForm as any)[field.name]}
                                  onChange={(e) => {
                                    const { value } = e.target;
                                    let error = "";

                                    // First/Last Name: No numbers (but allow empty)
                                    if (
                                      field.name === "firstname" ||
                                      field.name === "lastName"
                                    ) {
                                      if (value && /\d/.test(value)) {
                                        error = "Names cannot contain numbers";
                                      }
                                    }

                                    // Phone: Only digits, 10–12 characters (but allow empty)
                                    if (field.name === "phone") {
                                      if (value && !/^\d{10,12}$/.test(value)) {
                                        error =
                                          "Phone must be 10–12 digits and only contain numbers";
                                      }
                                    }

                                    // Email: Basic format check (but allow empty)
                                    if (field.name === "email") {
                                      const emailRegex =
                                        /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
                                      if (value && !emailRegex.test(value)) {
                                        error =
                                          "Please enter a valid email address";
                                      }
                                    }

                                    // Update error state
                                    setNameErrors((prev) => ({
                                      ...prev,
                                      [field.name]: error,
                                    }));

                                    // Update form state
                                    setEmployeeForm((prev) => ({
                                      ...prev,
                                      [field.name]: value,
                                    }));
                                  }}
                                />
                                {(field.name === "firstname" ||
                                  field.name === "lastName" ||
                                  field.name === "username" ||
                                  field.name === "phone" ||
                                  field.name === "email") &&
                                  nameErrors[field.name] && (
                                    <small className="text-danger">
                                      {nameErrors[field.name]}
                                    </small>
                                  )}
                              </div>
                            </div>
                          ))}
                          {/* 🔽 Password */}
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Password <span className="text-danger"> *</span>
                              </label>
                              <div className="pass-group">
                                <input
                                  type="text"
                                  className="pass-input form-control"
                                  value={employeeForm.password}
                                  onChange={(e) =>
                                    setEmployeeForm((prev) => ({
                                      ...prev,
                                      password: e.target.value,
                                    }))
                                  }
                                />
                              </div>
                            </div>
                          </div>

                          {/* 🔽 Joining Date */}
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Joining Date{" "}
                                <span className="text-danger"> *</span>
                              </label>
                              <div className="input-icon-end position-relative">
                                <DatePicker
                                  className="form-control datetimepicker"
                                  format="YYYY-MM-DD"
                                  value={
                                    employeeForm.joiningDate
                                      ? dayjs(
                                          employeeForm.joiningDate,
                                          "YYYY-MM-DD"
                                        )
                                      : null
                                  }
                                  onChange={(date) =>
                                    setEmployeeForm((prev) => ({
                                      ...prev,
                                      joiningDate: date
                                        ? dayjs(date).format("YYYY-MM-DD")
                                        : "",
                                    }))
                                  }
                                  getPopupContainer={getModalContainer}
                                  placeholder="YYYY-MM-DD"
                                />
                                <span className="input-icon-addon">
                                  <i className="ti ti-calendar text-gray-7" />
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* 🔽 Dropdown Selects */}
                          {[
                            {
                              label: "Role",
                              required: true,
                              options: roleName,
                              field: "role",
                            },
                            {
                              label: "Department",
                              required: true,
                              options: department,
                              field: "department",
                            },
                            {
                              label: "Designation",
                              required: true,
                              options: designation,
                              field: "designation",
                            },
                            {
                              label: "Job Type",
                              required: true,
                              options: jobType,
                              field: "jobType",
                            },
                          ].map((item, i) => {
                            const defaultOption = item.options.find(
                              (opt) =>
                                opt.value ===
                                employeeForm[
                                  item.field as keyof EmployeeFormType
                                ]
                            ) || { value: "", label: "Select" };
                            return (
                              <div className="col-md-6" key={i}>
                                <div className="mb-3">
                                  <label className="form-label">
                                    {item.label}
                                    <span className="text-danger"> *</span>
                                  </label>
                                  <CommonSelect
                                    className="select"
                                    options={item.options}
                                    value={
                                      item.options.find(
                                        (opt) =>
                                          opt.value ===
                                          employeeForm[
                                            item.field as keyof EmployeeFormType
                                          ]
                                      ) || { value: "", label: "Select" }
                                    }
                                    onChange={(selected) =>
                                      setEmployeeForm((prev) => ({
                                        ...prev,
                                        [item.field]: selected?.value || "",
                                      }))
                                    }
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* 🔽 Modal Footer */}
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-outline-light border me-2"
                          onClick={resetForm}
                        >
                          Reset
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSubmit}
                          disabled={isCreating}
                        >
                          {isCreating ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* 🔽 Backdrop */}
          <div
            className="modal-backdrop fade show"
            onClick={() => setShowAddModal(false)}
          />
        </>
      )}
      {showDeleteModal && (
        <>
          <div className="modal fade show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body text-center">
                  <p>
                    Are you sure you want to delete{" "}
                    <strong>{selectedEmployee?.Name}</strong>?
                  </p>
                  <div className="d-flex justify-content-center">
                    <button
                      className="btn btn-light me-2"
                      onClick={() => setShowDeleteModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={async () => {
                        await handleDelete();
                        setShowDeleteModal(false);
                      }}
                    >
                      Yes, Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            onClick={() => setShowDeleteModal(false)}
          />
        </>
      )}
    </>
  );
};

export default EmployeeList;
